import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  addComplaintDocument,
  addComplaintNote,
  getComplaintById,
  listComplaintDocuments,
  listComplaintNotes,
  markComplaintReceived,
  markComplaintUrgent,
  updateComplaintStatus,
} from "@/lib/api";

const statusOptions = ["Acknowledged", "InReview", "Resolved", "Archived"];

export const ComplaintDetailsPanel = ({ token, complaintId, role, onUpdated }) => {
  const [complaint, setComplaint] = useState(null);
  const [notes, setNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [docName, setDocName] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!complaintId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [complaintData, noteData, documentData] = await Promise.all([
          getComplaintById(token, complaintId),
          listComplaintNotes(token, complaintId),
          listComplaintDocuments(token, complaintId),
        ]);
        setComplaint(complaintData);
        setNotes(noteData);
        setDocuments(documentData);
      } catch {
        toast.error("Unable to load complaint details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [complaintId, token]);

  if (!complaintId) {
    return (
      <div className="card" data-testid="complaint-detail-empty">
        <div className="card-head">
          <h3>Complaint Detail</h3>
        </div>
        <div className="card-body">Select a complaint from list to inspect full details.</div>
      </div>
    );
  }

  if (loading || !complaint) {
    return (
      <div className="card" data-testid="complaint-detail-loading">
        <div className="card-body">Loading complaint details...</div>
      </div>
    );
  }

  const updateStatus = async (status) => {
    try {
      await updateComplaintStatus(token, complaintId, { status });
      toast.success(`Status changed to ${status}`);
      onUpdated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to update status");
    }
  };

  const toggleUrgent = async () => {
    try {
      await markComplaintUrgent(token, complaintId, { urgent: !complaint.urgency_flag });
      toast.success("Urgency updated");
      onUpdated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to update urgency");
    }
  };

  const receivedPhysical = async () => {
    try {
      await markComplaintReceived(token, complaintId);
      toast.success("Marked as physically received");
      onUpdated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to mark received");
    }
  };

  const addNote = async () => {
    if (!noteInput.trim()) {
      toast.error("Enter note text");
      return;
    }
    try {
      await addComplaintNote(token, complaintId, { note_text: noteInput.trim() });
      setNoteInput("");
      toast.success("Note added");
      onUpdated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to add note");
    }
  };

  const addDocument = async () => {
    if (!docName.trim() || !docUrl.trim()) {
      toast.error("Provide file name and file URL");
      return;
    }
    try {
      await addComplaintDocument(token, complaintId, { file_name: docName.trim(), file_url: docUrl.trim() });
      setDocName("");
      setDocUrl("");
      toast.success("Document linked");
      onUpdated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to add document");
    }
  };

  return (
    <div className="card" data-testid="complaint-detail-panel">
      <div className="card-head">
        <h3>Complaint Detail: {complaint.complaint_id}</h3>
      </div>
      <div className="card-body detail-grid">
        <div className="detail-box" data-testid="detail-core-box">
          <p><strong>Status:</strong> {complaint.status}</p>
          <p><strong>Level:</strong> {complaint.current_level}</p>
          <p><strong>Urgent:</strong> {complaint.urgency_flag ? "Yes" : "No"}</p>
          <p><strong>Category:</strong> {complaint.category}</p>
          <p><strong>Applicant:</strong> {complaint.applicant_name} ({complaint.applicant_mobile})</p>
          <p><strong>Booth:</strong> {complaint.booth_id || "N/A"}</p>
          <p><strong>Organization:</strong> {complaint.organization_id || "N/A"}</p>
          <p><strong>Escalation Reason:</strong> {complaint.escalation_reason}</p>
        </div>

        <div className="detail-box" data-testid="detail-actions-box">
          <p className="detail-heading">Lifecycle Actions</p>
          <div className="action-wrap">
            {statusOptions.map((status) => (
              <Button key={status} variant="outline" onClick={() => updateStatus(status)} data-testid={`status-action-${status}`}>
                {status}
              </Button>
            ))}
          </div>

          {role !== "booth_incharge" ? (
            <Button variant="outline" onClick={toggleUrgent} data-testid="toggle-urgent-button">
              {complaint.urgency_flag ? "Remove Urgent Flag" : "Mark Urgent"}
            </Button>
          ) : (
            <Button variant="outline" onClick={receivedPhysical} data-testid="mark-received-button">
              Mark Received Physically
            </Button>
          )}
        </div>

        <div className="detail-box" data-testid="detail-note-box">
          <p className="detail-heading">Progress Notes</p>
          <div className="input-row">
            <input
              value={noteInput}
              onChange={(event) => setNoteInput(event.target.value)}
              placeholder="Add progress note"
              data-testid="note-input"
            />
            <Button onClick={addNote} data-testid="add-note-button">Add</Button>
          </div>
          <ul className="detail-list">
            {notes.map((note) => (
              <li key={note.id} data-testid={`note-item-${note.id}`}>{note.note_text}</li>
            ))}
          </ul>
        </div>

        <div className="detail-box" data-testid="detail-document-box">
          <p className="detail-heading">Documents</p>
          <div className="input-row stacked">
            <input value={docName} onChange={(event) => setDocName(event.target.value)} placeholder="File name" data-testid="doc-name-input" />
            <input value={docUrl} onChange={(event) => setDocUrl(event.target.value)} placeholder="File URL" data-testid="doc-url-input" />
            <Button onClick={addDocument} data-testid="add-document-button">Attach Document</Button>
          </div>
          <ul className="detail-list">
            {documents.map((doc) => (
              <li key={doc.id} data-testid={`doc-item-${doc.id}`}>
                <a href={doc.file_url} target="_blank" rel="noreferrer">{doc.file_name}</a>
              </li>
            ))}
          </ul>
        </div>

        <div className="detail-box full" data-testid="detail-application-box">
          <p className="detail-heading">Drafted Application</p>
          <p className="draft-text">{complaint.drafted_application_text}</p>
        </div>
      </div>
    </div>
  );
};
